import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Filter, Check, ChevronDown, Banknote, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useCurrency } from '@/context/CurrencyContext';

interface FilterBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: {
    priceRange: [number, number];
    selectedBrands: string[];
    selectedCategories: string[];
  }) => void;
  
  // Current filter values (to initialize the sheet)
  initialPriceRange: [number, number];
  initialSelectedBrands: string[];
  initialSelectedCategories: string[];
  maxPrice: number;
  
  // Data for filter options
  availableBrands: string[];
  availableCategories: string[];
  isLoadingBrands: boolean;
  isLoadingCategories: boolean;
  errorBrands?: string;
  errorCategories?: string;
}

const FilterBottomSheet: React.FC<FilterBottomSheetProps> = ({
  isOpen,
  onClose,
  onApply,
  initialPriceRange,
  initialSelectedBrands,
  initialSelectedCategories,
  maxPrice,
  availableBrands,
  availableCategories,
  isLoadingBrands,
  isLoadingCategories,
  errorBrands,
  errorCategories,
}) => {
  const { formatPrice } = useCurrency();
  
  // Local state for filter values (not applied until user clicks Apply)
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>(initialPriceRange);
  const [localSelectedBrands, setLocalSelectedBrands] = useState<string[]>(initialSelectedBrands);
  const [localSelectedCategories, setLocalSelectedCategories] = useState<string[]>(initialSelectedCategories);
  
  // Collapsible sections state - All collapsed by default
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);

  // Reset local state when sheet opens
  React.useEffect(() => {
    if (isOpen) {
      setLocalPriceRange(initialPriceRange);
      setLocalSelectedBrands(initialSelectedBrands);
      setLocalSelectedCategories(initialSelectedCategories);
    }
  }, [isOpen, initialPriceRange, initialSelectedBrands, initialSelectedCategories]);

  const handleBrandToggle = (brand: string) => {
    setLocalSelectedBrands(prev => 
      prev.includes(brand) 
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const handleCategoryToggle = (category: string) => {
    setLocalSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleApply = () => {
    onApply({
      priceRange: localPriceRange,
      selectedBrands: localSelectedBrands,
      selectedCategories: localSelectedCategories,
    });
    onClose();
  };

  const handleReset = () => {
    // Reset local state
    setLocalPriceRange([0, maxPrice]);
    setLocalSelectedBrands([]);
    setLocalSelectedCategories([]);
    
    // Check if there are actually active filters on the page (from initial props)
    const hasActivePageFilters = (
      (initialPriceRange[0] > 0 || initialPriceRange[1] < maxPrice) ||
      initialSelectedBrands.length > 0 ||
      initialSelectedCategories.length > 0
    );
    
    if (hasActivePageFilters) {
      // Apply the reset immediately and close sheet
      onApply({
        priceRange: [0, maxPrice],
        selectedBrands: [],
        selectedCategories: [],
      });
      onClose();
    }
    // If no active filters on page, just clear local selections and keep sheet open
  };

  const getTotalFilterCount = () => {
    let count = 0;
    if (localPriceRange[0] > 0 || localPriceRange[1] < maxPrice) count++;
    count += localSelectedBrands.length;
    count += localSelectedCategories.length;
    return count;
  };

  const hasActiveFilters = () => {
    return (localPriceRange[0] > 0 || localPriceRange[1] < maxPrice) || 
           localSelectedBrands.length > 0 || 
           localSelectedCategories.length > 0;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
          />
          
          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 400, damping: 40 }}
            className="fixed inset-x-0 bottom-0 z-[9999] flex h-[85vh] max-h-[85vh] flex-col rounded-t-3xl border-t border-gray-200 bg-white shadow-2xl"
          >
            {/* Handle and Close Button */}
            <div className="relative flex items-center justify-center pt-4 pb-2">
              <div className="h-1.5 w-12 rounded-full bg-gray-300" />
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="absolute right-4 top-2 h-8 w-8 rounded-full hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-jamaica-green to-jamaica-green/80">
                  <Filter className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 font-sans">Filter Products</h2>
                  <p className="text-sm text-gray-500">
                    {getTotalFilterCount() > 0 ? `${getTotalFilterCount()} filters selected` : 'No filters applied'}
                  </p>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-6 pb-20"> {/* Extra padding for fixed apply button */}
                
                {/* Price Range Filter */}
                <Collapsible open={isPriceOpen} onOpenChange={setIsPriceOpen} className="border-b border-gray-100/80 pb-6">
                  <CollapsibleTrigger className="flex justify-between items-center w-full py-3 group hover:bg-gray-50/50 rounded-lg px-2 transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-lg flex items-center justify-center group-hover:from-jamaica-green/30 group-hover:to-jamaica-green/20 transition-all duration-300">
                        <Banknote className="h-4 w-4 text-jamaica-green" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-700 group-hover:text-jamaica-green transition-colors duration-300">
                        Price Range
                      </h4>
                      {(localPriceRange[0] > 0 || localPriceRange[1] < maxPrice) && (
                        <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-none text-xs shadow-sm animate-pulse">Active</Badge>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-jamaica-green transition-all duration-300 group-data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 px-2">
                    <div className="space-y-5">
                      <Slider 
                        defaultValue={[localPriceRange[0], localPriceRange[1]]} 
                        min={0}
                        max={maxPrice}
                        step={5}
                        value={[localPriceRange[0], localPriceRange[1]]}
                        onValueChange={(value) => setLocalPriceRange([value[0], value[1]])}
                        className="my-6" 
                      />
                      <div className="flex items-center justify-between gap-3">
                        <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 shadow-sm flex-1 text-center">
                          {formatPrice(localPriceRange[0])}
                        </div>
                        <div className="w-4 h-px bg-gray-300"></div>
                        <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 shadow-sm flex-1 text-center">
                          {formatPrice(localPriceRange[1])}
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Categories Filter - Moved above brands */}
                <Collapsible open={isCategoriesOpen} onOpenChange={setIsCategoriesOpen} className="border-b border-gray-100/80 pb-6">
                  <CollapsibleTrigger className="flex justify-between items-center w-full py-3 group hover:bg-gray-50/50 rounded-lg px-2 transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-lg flex items-center justify-center group-hover:from-jamaica-green/30 group-hover:to-jamaica-green/20 transition-all duration-300">
                        <Tag className="h-4 w-4 text-jamaica-green" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-700 group-hover:text-jamaica-green transition-colors duration-300">
                        Categories
                      </h4>
                      {localSelectedCategories.length > 0 && (
                        <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-none text-xs shadow-sm">
                          {localSelectedCategories.length}
                        </Badge>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-jamaica-green transition-all duration-300 group-data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 px-2">
                    {isLoadingCategories ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2 text-sm text-gray-500">Loading categories...</span>
                      </div>
                    ) : errorCategories ? (
                      <div className="text-center py-8 text-red-500 text-sm">{errorCategories}</div>
                    ) : (
                      <div className="space-y-3">
                        {availableCategories.map((category) => (
                          <div key={category} className="flex items-center space-x-3 group cursor-pointer hover:bg-gray-50/50 rounded-lg p-2 transition-all duration-200">
                            <Checkbox
                              id={`category-${category}`}
                              checked={localSelectedCategories.includes(category)}
                              onCheckedChange={() => handleCategoryToggle(category)}
                              className="data-[state=checked]:bg-jamaica-green data-[state=checked]:border-jamaica-green transition-all duration-200"
                            />
                            <label
                              htmlFor={`category-${category}`}
                              className="text-sm font-medium text-gray-700 cursor-pointer group-hover:text-jamaica-green transition-colors duration-200 flex-1"
                            >
                              {category.toUpperCase()}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Brands Filter - Moved below categories */}
                <Collapsible open={isBrandsOpen} onOpenChange={setIsBrandsOpen} className="border-b border-gray-100/80 pb-6">
                  <CollapsibleTrigger className="flex justify-between items-center w-full py-3 group hover:bg-gray-50/50 rounded-lg px-2 transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-lg flex items-center justify-center group-hover:from-jamaica-green/30 group-hover:to-jamaica-green/20 transition-all duration-300">
                        <Filter className="h-4 w-4 text-jamaica-green" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-700 group-hover:text-jamaica-green transition-colors duration-300">
                        Brands
                      </h4>
                      {localSelectedBrands.length > 0 && (
                        <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-none text-xs shadow-sm">
                          {localSelectedBrands.length}
                        </Badge>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-jamaica-green transition-all duration-300 group-data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 px-2">
                    {isLoadingBrands ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2 text-sm text-gray-500">Loading brands...</span>
                      </div>
                    ) : errorBrands ? (
                      <div className="text-center py-8 text-red-500 text-sm">{errorBrands}</div>
                    ) : (
                      <div className="space-y-3">
                        {availableBrands.map((brand) => (
                          <div key={brand} className="flex items-center space-x-3 group cursor-pointer hover:bg-gray-50/50 rounded-lg p-2 transition-all duration-200">
                            <Checkbox
                              id={`brand-${brand}`}
                              checked={localSelectedBrands.includes(brand)}
                              onCheckedChange={() => handleBrandToggle(brand)}
                              className="data-[state=checked]:bg-jamaica-green data-[state=checked]:border-jamaica-green transition-all duration-200"
                            />
                            <label
                              htmlFor={`brand-${brand}`}
                              className="text-sm font-medium text-gray-700 cursor-pointer group-hover:text-jamaica-green transition-colors duration-200 flex-1"
                            >
                              {brand}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>

            {/* Fixed Bottom Action Bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={!hasActiveFilters()}
                  className={`flex-1 border-2 font-semibold transition-all duration-200 ${
                    hasActiveFilters()
                      ? 'border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600'
                      : 'border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                >
                  <X className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={!hasActiveFilters()}
                  className={`flex-[2] font-semibold py-3 shadow-lg transition-all duration-200 ${
                    hasActiveFilters()
                      ? 'bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:shadow-xl transform hover:-translate-y-0.5'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Apply Filters
                  {getTotalFilterCount() > 0 && (
                    <Badge className="ml-2 bg-white/20 text-white border-0">
                      {getTotalFilterCount()}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FilterBottomSheet;